import { useMemo } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import meanBy from 'lodash/meanBy';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const NODE_STORAGE_CURRENT_METRICS_QUERY_KEY = 'node-storage-current-metrics';
const getNodeStorageCurrentMetricsQueryKey = (params: {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  storageType: string;
}) =>
  [
    NODE_STORAGE_CURRENT_METRICS_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.nodeName,
    params.storageType,
  ] as const;

type NodeStorageCurrentMetricsQueryParams = {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  storageType: string;
  enabled?: boolean;
};

const getNodeStorageCurrentMetricsQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  storageType,
  enabled = true,
}: NodeStorageCurrentMetricsQueryParams) =>
  queryOptions({
    queryKey: getNodeStorageCurrentMetricsQueryKey({
      organizationId,
      clusterId,
      nodeName,
      storageType,
    }),
    queryFn: async () => {
      const endTime = dayjs().toISOString();
      const startTime = dayjs().subtract(30, 'minute').toISOString();

      const { data } = await apiClient.stop.getNodeStorageMetrics({
        organizationId: organizationId!,
        clusterId: clusterId!,
        nodeName: nodeName!,
        storageType,
        startTime,
        endTime,
        step: '5m',
      });

      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, !!nodeName, enabled),
  });

type UseNodeStorageCurrentMetricsQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  storageType: string;
  enabled?: boolean;
};

export const useNodeStorageCurrentMetricsQuery = ({
  clusterId,
  nodeName,
  storageType,
  enabled,
}: UseNodeStorageCurrentMetricsQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getNodeStorageCurrentMetricsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      nodeName,
      storageType,
      enabled,
    })
  );

  const currentAvg = useMemo(() => {
    const series = data?.series;

    if (!series?.length) {
      return undefined;
    }

    return {
      iops: meanBy(series, (s) => s.iops || 0),
      provisionedIops: meanBy(series, (s) => s.provisionedIops || 0),
      throughputBytes: meanBy(series, (s) => s.throughputBytes || 0),
      provisionedThroughputBytes: meanBy(
        series,
        (s) => s.provisionedThroughputBytes || 0
      ),
      usedBytes: meanBy(series, (s) => (s.usedBytes ? Number(s.usedBytes) : 0)),
      totalBytes: meanBy(series, (s) =>
        s.totalBytes ? Number(s.totalBytes) : 0
      ),
    };
  }, [data]);

  return {
    currentAvg,
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
